"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { entryById, type ChordEntry } from "@/lib/music/chord-catalog";
import { studioViewPath } from "@/lib/studio/studio-routes";

const CHORDS_PATH = studioViewPath("chords");

type PracticeMode = "static" | "tempo";

interface ChordsContextValue {
  /** Accord en cours de pratique, dérivé de `?practice=`. */
  currentEntry: ChordEntry | null;
  /** Map<chordId, Set<positionId>>, progression par accord, scopée à la session. */
  progress: Record<string, ReadonlySet<string>>;
  /** Positions validées pour l'accord courant (calculé). */
  currentDone: ReadonlySet<string>;
  /** Mode d'exercice : grille statique ou drill tempo. Reset à "static"
   *  à chaque changement d'accord. */
  practiceMode: PracticeMode;
  setPracticeMode: (mode: PracticeMode) => void;
  /** Marque une position comme validée pour un accord donné. Idempotent. */
  markDone: (chordId: string, posId: string) => void;
  /** Sélectionne un nouvel accord à pratiquer (push URL). */
  selectEntry: (entry: ChordEntry) => void;
  /** Quitte le flow practice (retire `?practice=`). */
  closePractice: () => void;
}

const ChordsContext = createContext<ChordsContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

/**
 * Provider du module Chords. Centralise la progression d'exercice et le
 * mode practice (static / tempo) pour la sidebar (`PracticeStructureSidebar`)
 * et l'overlay (`ChordsOverlay`).
 *
 * L'accord courant est lu depuis `?practice=…`, l'URL reste la source de
 * vérité pour la navigation entre accords (cohérent avec les call sites
 * externes : harmony, briefing, chord-library).
 *
 * Doit envelopper les deux consommateurs pour qu'ils partagent la même
 * source. Monté en permanence dans `StudioPage` pour stabiliser l'arbre
 * React du `<StudioFrame>` à travers les changements de mode, le
 * Provider est inerte hors mode chords (juste du state local + lecture
 * de `?practice=`).
 */
export function ChordsProvider({ children }: ProviderProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const practiceParam = searchParams?.get("practice") ?? null;

  const currentEntry = useMemo(
    () => (practiceParam ? entryById(decodeURIComponent(practiceParam)) : null),
    [practiceParam],
  );

  const [progress, setProgress] = useState<Record<string, Set<string>>>({});
  const [practiceMode, setPracticeMode] = useState<PracticeMode>("static");

  // Reset to the static drill whenever the active chord changes, preserve
  // the per-chord done set, but always start a fresh chord on the static
  // screen. Implemented via the "store previous prop in state" pattern
  // (React docs: "Adjusting state when a prop changes") so the reset is
  // applied during render instead of in an effect, avoids a cascading
  // re-render and keeps the static UI from flashing the wrong mode.
  const [prevEntryId, setPrevEntryId] = useState<string | null>(
    currentEntry?.id ?? null,
  );

  if (prevEntryId !== (currentEntry?.id ?? null)) {
    setPrevEntryId(currentEntry?.id ?? null);
    setPracticeMode("static");
  }

  const markDone = useCallback((chordId: string, posId: string) => {
    setProgress((prev) => {
      const existing = prev[chordId] ?? new Set<string>();

      if (existing.has(posId)) {
        return prev;
      }

      const next = new Set(existing);
      next.add(posId);

      return { ...prev, [chordId]: next };
    });
  }, []);

  const selectEntry = useCallback(
    (entry: ChordEntry) => {
      router.push(`${CHORDS_PATH}?practice=${encodeURIComponent(entry.id)}`);
    },
    [router],
  );

  // Quitter le flow practice retire `?practice=` mais reste sur la vue
  // Chords : c'est l'exercice qu'on ferme, pas l'onglet.
  const closePractice = useCallback(() => {
    router.push(CHORDS_PATH);
  }, [router]);

  const currentDone = useMemo<ReadonlySet<string>>(
    () =>
      currentEntry ? progress[currentEntry.id] ?? new Set<string>() : new Set<string>(),
    [currentEntry, progress],
  );

  const value = useMemo<ChordsContextValue>(
    () => ({
      currentEntry,
      progress,
      currentDone,
      practiceMode,
      setPracticeMode,
      markDone,
      selectEntry,
      closePractice,
    }),
    [
      currentEntry,
      progress,
      currentDone,
      practiceMode,
      markDone,
      selectEntry,
      closePractice,
    ],
  );

  return (
    <ChordsContext.Provider value={value}>{children}</ChordsContext.Provider>
  );
}

export function useChordsContext(): ChordsContextValue {
  const ctx = useContext(ChordsContext);

  if (!ctx) {
    throw new Error(
      "useChordsContext doit être utilisé à l'intérieur d'un ChordsProvider",
    );
  }

  return ctx;
}
